import { useMemo } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatVND } from '@/lib/utils';
import { reconcileWallets } from '../../../finance';
import { useData } from '../../store/data';
import { useTxLogs } from './hooks';
import { useAssetsUI } from './useAssetsUI';

/**
 * Đối soát: so số dư đang lưu với số dư suy ra từ lịch sử. CHỈ BÁO — không tự sửa.
 * Lệch phần lớn là biến động cũ không truy được; muốn khép thì dùng "Điều chỉnh số dư".
 */
export default function ReconcileTab({ debtPayments, active }: { debtPayments: any[]; active: boolean }) {
  const { sources, creditPayments } = useData(s => s.data);
  const { rows: txLogs, error } = useTxLogs(active);
  const openSheet = useAssetsUI(s => s.open);

  const result = useMemo(
    () => (txLogs ? reconcileWallets({ sources, txLogs, creditPayments, debtPayments }) : null),
    [sources, txLogs, creditPayments, debtPayments]
  );

  if (error) return <p role="alert" className="text-sm text-bad">Không tải được lịch sử: {error}</p>;
  if (!result) return <div className="space-y-2">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}</div>;

  const bad = result.filter(r => !r.matched);
  return (
    <div className="space-y-4">
      <Card className="space-y-1 p-4 text-sm">
        <p className="font-medium">
          {bad.length === 0 ? 'Mọi ví khớp với lịch sử.' : `${result.length - bad.length}/${result.length} ví khớp với lịch sử; ${bad.length} ví lệch.`}
        </p>
        <p className="text-muted-foreground">
          Số dư kỳ vọng được suy ra từ lịch sử giao dịch (cộng số dư đầu kỳ, trừ trả thẻ và trả nợ). Khoảng lệch thường là biến động cũ không còn dấu vết
          (số dư đầu kỳ, chỉnh tay, dữ liệu trước khi có lịch sử). Công cụ này <b>chỉ báo, không tự sửa</b> — khi bạn đã biết số thực tế, dùng "Điều chỉnh"
          để ghi lại kèm lý do; lần điều chỉnh làm mốc mới, các lần đối soát sau chỉ tính biến động từ mốc đó.
        </p>
      </Card>

      <Card className="divide-y divide-border overflow-hidden p-0">
        <div className="hidden grid-cols-[1fr_8rem_8rem_8rem_6rem] gap-3 bg-muted/50 px-4 py-2 text-xs font-medium text-muted-foreground md:grid">
          <span>Ví</span><span className="text-right">Đang ghi</span><span className="text-right">Theo lịch sử</span><span className="text-right">Chênh</span><span />
        </div>
        {result.map(r => (
          <div key={r.id} className="grid items-center gap-x-3 gap-y-1 px-4 py-3 md:grid-cols-[1fr_8rem_8rem_8rem_6rem]">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{r.name}</p>
              <p className="text-xs text-muted-foreground">{r.type === 'credit' ? 'Thẻ — dư nợ' : 'Số dư'}{r.anchored && ' · đã có mốc điều chỉnh'}</p>
            </div>
            <span className="num text-sm md:text-right"><span className="text-xs text-muted-foreground md:hidden">Đang ghi: </span>{formatVND(r.stored)}</span>
            <span className="num text-sm md:text-right"><span className="text-xs text-muted-foreground md:hidden">Theo lịch sử: </span>{formatVND(r.expected)}</span>
            <span className={cn('num text-sm font-medium md:text-right', r.matched ? 'text-good' : 'text-warn')}>
              {r.matched ? <span className="inline-flex items-center gap-1"><CheckCircle2 size={14} aria-hidden /> Khớp</span> : `${r.diff > 0 ? '+' : '−'}${formatVND(Math.abs(r.diff))}`}
            </span>
            <div className="md:text-right">
              {!r.matched && <Button variant="outline" size="sm" onClick={() => openSheet({ kind: 'adjust', id: r.id })}>Điều chỉnh</Button>}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
